// src/theme/theme.ts
import { css } from '@emotion/react';

const theme = {
  colors: {
    primary: {
      100: '#F6F9E8',
      200: '#EDF3D1',
      300: '#E4EEBA',
      400: '#DBE8A3',
      500: '#D3E28C',
      600: '#CADC75',
      700: '#C1D65E',
      800: '#B8D147',
      900: '#AFCB30',
      1000: '#A5C614',
      1500: '#7A940F',
      2000: '#526308',
      2500: '#3D4A06',
      main: '#AFCB30',
      contrastText: '#FFFFFF',
    },
    secondary: {
      100: '#F5EEF5',
      200: '#EBDDEB',
      300: '#E1CCE1',
      400: '#D7BBD7',
      500: '#CDA9CD',
      600: '#C498C4',
      700: '#BA87BA',
      800: '#B076B0',
      900: '#A665A6',
      1000: '#9C549C',
      main: '#9C549C',
      contrastText: '#000000',
    },
    tertiary: {
      100: '#E9E8E9',
      200: '#D2D2D4',
      300: '#BCBBBE',
      400: '#A6A5A8',
      500: '#8F8E92',
      600: '#79787D',
      700: '#636267',
      800: '#4D4B51',
      900: '#36353C',
      1000: '#201E26',
      main: '#4D4B51',
      contrastText: '#FFFFFF',
    },
    neutral: {
      white: '#FFFFFF',
      greyLight: {
        100: '#F1F1F1',
        200: '#E3E3E3',
        300: '#D6D6D6',
        400: '#C8C8C8',
        500: '#BABABA',
        600: '#ACACAC',
        700: '#9E9E9E',
        800: '#919191',
        900: '#838383',
        1000: '#757575',
      },
      greyDark: {
        100: '#EDEDED',
        200: '#DBDBDB',
        300: '#C9C9C9',
        400: '#B7B7B7',
        500: '#A4A4A4',
        600: '#929292',
        700: '#808080',
        800: '#6E6E6E',
        900: '#5C5C5C',
        1000: '#4A4A4A',
      },
    },
    // Text opacity levels (white with alpha for emphasis hierarchy)
    text: {
      highEmphasis: '#FFFFFF',              // 100% - Títulos principales
      mediumEmphasis: 'rgba(255, 255, 255, 0.8)',  // 80% - Texto de cuerpo
      mediumLowEmphasis: 'rgba(255, 255, 255, 0.64)', // 64% - Labels
      disabled: 'rgba(255, 255, 255, 0.4)', // 40% - Placeholders
    },
    // Background opacity levels (white with alpha for overlays and effects)
    overlay: {
      indicator: 'rgba(255, 255, 255, 0.6)',  // 60% - Home indicator
      hover: 'rgba(255, 255, 255, 0.05)',     // 5% - Hover states
    },
    accentSecondary: '#6ac9ce',
    accent: {
      A: {
        100: '#F2E9FA',
        200: '#E5D3F5',
        300: '#D8BDF0',
        400: '#CBA7EB',
        500: '#BE90E7',
        600: '#B27AE2',
        700: '#A564DD',
        800: '#984ED8',
        900: '#8B38D3',
        1000: '#7E22CE',
      },
      B: {
        100: '#F1FAFB',
        200: '#E1F4F5',
        300: '#D3EFF1',
        400: '#C3E9EB',
        500: '#B4E3E6',
        600: '#A6DFE2',
        700: '#97DADD',
        800: '#88D4D8',
        900: '#79CFD3',
        1000: '#6AC9CE',
      },
      C: {
        100: '#F8F6F2',
        200: '#F0ECE3',
        300: '#E9E2D6',
        400: '#E1D8C7',
        500: '#D9CEB9',
        600: '#D3C5AC',
        700: '#CCBC9E',
        800: '#C4B190',
        900: '#BDA882',
        1000: '#B59E74',
      },
    },
    state: { // Feedback colors
      success: {
        100: '#EBF6EB',
        200: '#D8EED7',
        300: '#C4E5C2',
        400: '#B0DDAE',
        500: '#9CD59A',
        600: '#89CC86',
        700: '#75C372',
        800: '#61BB5D',
        900: '#4EB349',
        1000: '#3AAA35',
      },
      alert: {
        100: '#FDEBE7',
        200: '#FAD6CE',
        300: '#F8C2B6',
        400: '#F5AD9E',
        500: '#F39985',
        600: '#F1856D',
        700: '#EE7055',
        800: '#EC5C3D',
        900: '#E94724',
        1000: '#E7330C',
      },
      warning: {
        100: '#FFF4E9',
        200: '#FFE8D4',
        300: '#FFDDBE',
        400: '#FFD1A9',
        500: '#FFC693',
        600: '#FFBB7E',
        700: '#FFAF69',
        800: '#FFA453',
        900: '#FF983E',
        1000: '#FF8D28',
      },
      informative: {
        100: '#E7F4FB',
        200: '#CDE7F5',
        300: '#B5DCF1',
        400: '#9CCFEB',
        500: '#82C3E6',
        600: '#6AB8E2',
        700: '#52ACDD',
        800: '#39A0D8',
        900: '#2094D3',
        1000: '#0788CE',
      },
      textLink: '#0788CE', // Specific Text link color
    },
    // Linear gradients
    backgroundLinearDegradado: {
      1000: '#36353C',
      linear: {
        start: '#36353C',
        end: '#201E26',
      },
    },
    moradoButtonsLinearDegradado: {
      1000: '#FF17FF',
      linear: {
        start: '#FF17FF',
        end: '#6F1E6F',
      },
    },
    greenButtonsLinearDegradado: {
      1000: '#B9CE4C',
      linear: {
        start: '#B9CE4C',
        end: '#A6C51A',
      },
    },
    headerBackgroundDegradado: {
      start: '#000000',
      middle: '#08287B',
      end: '#A6C519',
    },
    loginBackgroundDegradado: {
      start: '#A5C614', // Same as primary[1000]
      middle: '#667C26',
      end: '#263238',
      angle: '191.71deg',
    }
  },
  typography: {
    fontFamily: '"Open Sans", sans-serif',
    // Headings
    h1: {
      fontFamily: '"Open Sans", sans-serif',
      fontWeight: 600, // SemiBold
      fontSize: '3.75rem', // 60px
      lineHeight: 1.3,
      letterSpacing: '0px',
    },
    h2: {
      fontFamily: '"Open Sans", sans-serif',
      fontWeight: 600, // SemiBold
      fontSize: '2.5rem', // 40px
      lineHeight: 1.3,
      letterSpacing: '0px',
    },
    h3: {
      fontFamily: '"Open Sans", sans-serif',
      fontWeight: 600, // SemiBold
      fontSize: '2.125rem', // 34px
      lineHeight: 1.3,
      letterSpacing: '0px',
    },
    h4: {
      fontFamily: '"Open Sans", sans-serif',
      fontWeight: 600, // SemiBold
      fontSize: '1.875rem', // 30px
      lineHeight: 1.3,
      letterSpacing: '0px',
    },
    h5: {
      fontFamily: '"Open Sans", sans-serif',
      fontWeight: 600, // SemiBold
      fontSize: '1.75rem', // 28px
      lineHeight: 1.3,
      letterSpacing: '0px',
    },
    h6: {
      fontFamily: '"Open Sans", sans-serif',
      fontWeight: 600, // SemiBold
      fontSize: '1.50rem', // 24px
      lineHeight: 1.3,
      letterSpacing: '0px',
    },
    // Body
    body1Bold: {
      fontFamily: '"Open Sans", sans-serif',
      fontWeight: 700, // Bold
      fontSize: '1.25rem', // 20px
      lineHeight: 1.3,
      letterSpacing: '0px',
    },
    body1Regular: {
      fontFamily: '"Open Sans", sans-serif',
      fontWeight: 400, // Regular
      fontSize: '1.25rem', // 20px
      lineHeight: 1.3,
      letterSpacing: '0px',
    },
    body2Bold: {
      fontFamily: '"Open Sans", sans-serif',
      fontWeight: 700, // Bold
      fontSize: '1.125rem', // 18px
      lineHeight: 1.3,
      letterSpacing: '0px',
    },
    body2Regular: {
      fontFamily: '"Open Sans", sans-serif',
      fontWeight: 400, // Regular
      fontSize: '1.125rem', // 18px
      lineHeight: 1.3,
      letterSpacing: '0px',
    },
    body3Bold: {
      fontFamily: '"Open Sans", sans-serif',
      fontWeight: 700, // Bold
      fontSize: '1rem', // 16px
      lineHeight: 1.3,
      letterSpacing: '0px',
    },
    body3Regular: {
      fontFamily: '"Open Sans", sans-serif',
      fontWeight: 400, // Regular
      fontSize: '1rem', // 16px
      lineHeight: 1.3,
      letterSpacing: '0px',
    },
    body4Bold: {
      fontFamily: '"Open Sans", sans-serif',
      fontWeight: 700, // Bold
      fontSize: '0.875rem', // 14px
      lineHeight: 1.3,
      letterSpacing: '0px',
    },
    body4Regular: {
      fontFamily: '"Open Sans", sans-serif',
      fontWeight: 400, // Regular
      fontSize: '0.875rem', // 14px
      lineHeight: 1.3,
      letterSpacing: '0px',
    },
    // Caption
    captionBold: {
      fontFamily: '"Open Sans", sans-serif',
      fontWeight: 700, // Bold
      fontSize: '0.75rem', // 12px
      lineHeight: 1.3,
      letterSpacing: '0px',
    },
    captionRegular: {
      fontFamily: '"Open Sans", sans-serif',
      fontWeight: 400, // Regular
      fontSize: '0.75rem', // 12px
      lineHeight: 1.3,
      letterSpacing: '0px',
    },
  },
  // Breakpoints system (mobile-first approach)
  breakpoints: {
    values: {
      phone: 640,    // Small phones and up
      tablet: 768,   // Tablets and up
      desktop: 1024, // Desktop and up
      wide: 1280,    // Wide screens and up
    },
    // Helper for mobile-first media queries
    up: (breakpoint: 'phone' | 'tablet' | 'desktop' | 'wide') => {
      const values = {
        phone: 640,
        tablet: 768,
        desktop: 1024,
        wide: 1280,
      };
      return `@media (min-width: ${values[breakpoint]}px)`;
    },
    // Helper for desktop-first media queries (use sparingly)
    down: (breakpoint: 'phone' | 'tablet' | 'desktop' | 'wide') => {
      const values = {
        phone: 640,
        tablet: 768,
        desktop: 1024,
        wide: 1280,
      };
      return `@media (max-width: ${values[breakpoint] - 1}px)`;
    },
  },
  spacing: (factor: number) => `${8 * factor}px`,
  shadows: {
    none: 'none',
    xs: '0px 1px 2px rgba(0, 0, 0, 0.15)',
    sm: '0px 2px 4px rgba(0, 0, 0, 0.2)',
    md: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    lg: '0px 6px 8px rgba(0, 0, 0, 0.3)',
    xl: '0px 8px 16px rgba(0, 0, 0, 0.35)',
    '2xl': '0px 12px 24px rgba(0, 0, 0, 0.4)',
    // Shadows específicas para componentes
    card: '0px 2px 8px rgba(0, 0, 0, 0.15)',
    button: '0px 4px 16px rgba(0, 0, 0, 0.25)',
    modal: '0px 8px 32px rgba(0, 0, 0, 0.5)',
    dropdown: '0px 4px 12px rgba(0, 0, 0, 0.2)',
    // Shadows inset para cards con profundidad
    cardInset: '0px 1px 2px rgba(255, 255, 255, 0.1) inset, 0px 4px 16px rgba(0, 0, 0, 0.25) inset',
    // Shadows con blur más pronunciado
    soft: '0px 4px 24px rgba(0, 0, 0, 0.12)',
    strong: '0px 10px 40px rgba(0, 0, 0, 0.45)',
  },
  shape: {
    borderRadius: '4px',
    borderRadiusLarge: '8px',
    borderRadiusXLarge: '16px',
    borderRadiusRound: '50%',
  },
};

export type AppTheme = typeof theme;

// Global styles (aplicados vía ThemeProvider)
export const globalStyles = css`
  button {
    outline: none;
  }

  button:focus {
    outline: none;
  }

  button:focus-visible {
    outline: none;
  }
`;

export default theme;

declare module '@emotion/react' {
  // eslint-disable-next-line
  export interface Theme extends AppTheme {}
}
